<template>
    <section class="bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-6">
      <h2 class="text-2xl font-bold text-center mb-6 text-gray-900 dark:text-white">
        Solicitudes de Aumento de Dosis
      </h2>
  
      <div v-if="solicitudes.length === 0" class="text-gray-500 text-center">No hay solicitudes pendientes.</div>
  
      <div
        v-for="solicitud in solicitudes"
        :key="solicitud.id"
        class="bg-white dark:bg-gray-800 shadow-lg p-6 rounded-lg border border-gray-200 dark:border-gray-700 mb-6"
      >
        <h3 class="text-xl font-semibold text-gray-800 dark:text-white mb-4">
          Paciente ID: {{ solicitud.pacienteNombre }}
        </h3>
  
        <div class="grid gap-4 md:grid-cols-2 text-sm text-gray-700 dark:text-gray-300">
          <div class="bg-white dark:bg-gray-700 p-4 rounded-lg border">
            <p><strong>Estado:</strong> {{ solicitud.estado }}</p>
            <p><strong>Fecha:</strong> {{ formatFecha(solicitud.fecha) }}</p>
          </div>
  
          <div class="bg-white dark:bg-gray-700 p-4 rounded-lg border">
            <p><strong>1. ¿Cómo catalogas el hambre del 1 al 10?</strong><br> 
              {{ solicitud.respuestas?.hambreNivel ?? 'N/A' }}
            </p>
          </div>
  
          <div class="bg-white dark:bg-gray-700 p-4 rounded-lg border">
            <p><strong>2. Luego de inyectarse, ¿cuándo reaparece el apetito?</strong><br>
              {{ solicitud.respuestas?.reaparicionApetito ?? 'N/A' }}
            </p>
          </div>
  
          <div class="bg-white dark:bg-gray-700 p-4 rounded-lg border">
            <p><strong>3. ¿Tienes algún síntoma luego de cada inyección (mareo, náuseas, vómitos, etc)?</strong><br>
              {{ solicitud.respuestas?.sintomasPostInyeccion ?? 'N/A' }}
            </p>
          </div>
  
          <div class="bg-white dark:bg-gray-700 p-4 rounded-lg border">
            <p><strong>4. ¿Se come toda la porción del plato o deja alguna porción?</strong><br>
              {{ solicitud.respuestas?.porcionComida ?? 'N/A' }}
            </p>
          </div>
  
          <div class="bg-white dark:bg-gray-700 p-4 rounded-lg border">
            <p><strong>5. ¿Estás tomando bastante agua?</strong><br>
              {{ solicitud.respuestas?.tomaAgua ?? 'N/A' }}
            </p>
          </div>
  
          <div class="bg-white dark:bg-gray-700 p-4 rounded-lg border">
            <p><strong>6. (Sólo para mujeres) ¿Estás por caer en período o estás pasando el período?</strong><br>
              {{ solicitud.respuestas?.periodoMujer ?? 'N/A' }}
            </p>
          </div>
        </div>
  
        <div class="flex justify-end gap-2 mt-6">
          <button
            v-if="solicitud.estado === 'pendiente'"
            @click="cambiarEstado(solicitud.id, 'aprobada')"
            class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
          >
            Aprobar
          </button>
          <button
            v-if="solicitud.estado === 'pendiente'"
            @click="cambiarEstado(solicitud.id, 'denegada')"
            class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Denegar
          </button>
        </div>
      </div>
    </section>
  </template>
  
  <script>
import { db } from '@/firebase';
import { collection, getDocs, updateDoc, doc, getDoc, Timestamp } from 'firebase/firestore';

export default {
  name: 'RevisarSolicitudesDosis',

  props: {
    paciente: {
      type: Object,
      required: false, // ✅ Puede venir desde el modal o no si es vista global
      default: null
    }
  },

  data() {
    return {
      solicitudes: []
    };
  },

  async mounted() {
    if (this.paciente) {
      await this.cargarSolicitudesPaciente();
    } else {
      await this.cargarTodasLasSolicitudes(); // caso global (como menú admin)
    }
  },

  watch: {
    paciente: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) this.cargarSolicitudesPaciente();
      }
    }
  },

  methods: {
    async cargarSolicitudesPaciente() {
      const pacienteId = this.paciente?.id || this.paciente?.uid;
      if (!pacienteId) return;

      const snapshot = await getDocs(collection(db, 'solicitudesDosisGlobales'));

      this.solicitudes = snapshot.docs
        .filter(docSnap => docSnap.data().pacienteId === pacienteId)
        .map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
    },

    async cargarTodasLasSolicitudes() {
      const snapshot = await getDocs(collection(db, 'solicitudesDosisGlobales'));

      const solicitudesConNombre = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();

        let pacienteNombre = 'Paciente no encontrado';
        try {
          const pacienteRef = doc(db, 'pacientes', data.pacienteId);
          const pacienteSnap = await getDoc(pacienteRef);
          if (pacienteSnap.exists()) {
            const p = pacienteSnap.data();
            pacienteNombre = `${p.nombre ?? ''} ${p.apellido ?? ''}`;
          }
        } catch (error) {
          console.warn('⚠️ Error obteniendo paciente:', error);
        }

        solicitudesConNombre.push({
          id: docSnap.id,
          ...data,
          pacienteNombre
        });
      }

      this.solicitudes = solicitudesConNombre;
    },

    async cambiarEstado(id, nuevoEstado) {
      const fechaRespuesta = Timestamp.now();

      await updateDoc(doc(db, 'solicitudesDosisGlobales', id), {
        estado: nuevoEstado,
        fechaRespuesta
      });

      const solicitud = this.solicitudes.find(s => s.id === id);
      if (solicitud) {
        solicitud.estado = nuevoEstado;
        solicitud.fechaRespuesta = fechaRespuesta;
      }
    },

    formatFecha(date) {
      return date?.toDate ? date.toDate().toLocaleString() : '';
    }
  }
};
</script>
